import {firstValueFrom} from "rxjs";
import {NavigationAction, NavigationActionPage} from "@app/core/shared/domain/entities/navigation-action";
import {DeepLinkService} from "@app/core/shared/infrastructure/interfaces/deep-link.service";
import {GetFavoritesListCodeUseCase} from "@app/core/lists/application/get-favorites-list-code.use-case";
import {DeepLink, DeepLinkUrl} from "@app/core/shared/domain/entities/deep-link";
import {Injectable} from "@angular/core";
import {IsLoggedInUseCase} from "@app/core/user/application/isLoggedIn.use-case";

/**
 * Use case for processing a DeepLink to determine the corresponding NavigationAction.
 */
@Injectable({
    providedIn: 'root'
})
export class GetDeepLinkNavigationActionUseCase {

    constructor(
        private deepLinkService: DeepLinkService,
        private getFavoritesListIdUseCase:GetFavoritesListCodeUseCase,
        private isLoggedInUseCase: IsLoggedInUseCase,
    ) {}

    /**
     * Get the navigation action from the deep link.
     * If the Deep Link matches a Navigation Action that requires the user to be logged in and the user is not logged in, it returns null.
     * @param deepLink
     */
    execute(deepLink: DeepLink): Promise<NavigationAction | null> {
        return this.mapDeepLinkToNavigationAction(deepLink);
    }

    private async mapDeepLinkToNavigationAction(deepLink: DeepLink): Promise<NavigationAction | null> {
        // If the user is not logged in, we don't allow to navigate to the protected urls.
        const protectedUrls = [
            DeepLinkUrl.ORDERS,
            DeepLinkUrl.COUPONS,
            DeepLinkUrl.USUAL_PURCHASE,
            DeepLinkUrl.COMPARATOR,
            DeepLinkUrl.CHANGE_EMAIL,
            DeepLinkUrl.CHANGE_PASSWORD,
            DeepLinkUrl.CHECKOUT,
            DeepLinkUrl.FAVORITES,
        ];
        if (protectedUrls.includes(deepLink.path) && !await firstValueFrom(this.isLoggedInUseCase.execute())) {
            return null;
        }
        switch (deepLink.path) {
            case DeepLinkUrl.HOME:
                return new NavigationAction(NavigationActionPage.HOME);
            case DeepLinkUrl.CATALOG:
                const parameters = {
                    categoryCode: deepLink.parameters?.categoryCode,
                    filters: deepLink.parameters?.filters,
                }
                return new NavigationAction(NavigationActionPage.CATALOG, parameters);
            case DeepLinkUrl.ORDERS:
                return new NavigationAction(NavigationActionPage.ORDERS);
            case DeepLinkUrl.COUPONS:
                return new NavigationAction(NavigationActionPage.COUPONS);
            case DeepLinkUrl.NEWS:
                return new NavigationAction(NavigationActionPage.NEWS);
            case DeepLinkUrl.PROMOTIONS:
                return new NavigationAction(NavigationActionPage.PROMOTIONS);
            case DeepLinkUrl.LISTS:
                return new NavigationAction(NavigationActionPage.LISTS);
            case DeepLinkUrl.USUAL_PURCHASE:
                return new NavigationAction(NavigationActionPage.USUAL_PURCHASE);
            case DeepLinkUrl.BEST_SELLERS:
                return new NavigationAction(NavigationActionPage.BEST_SELLERS);
            case DeepLinkUrl.COMPARATOR:
                return new NavigationAction(NavigationActionPage.COMPARATOR);
            case DeepLinkUrl.CHANGE_EMAIL:
                return new NavigationAction(NavigationActionPage.CHANGE_EMAIL);
            case DeepLinkUrl.CHANGE_PASSWORD:
                return new NavigationAction(NavigationActionPage.CHANGE_PASSWORD);
            case DeepLinkUrl.CHECKOUT:
                return new NavigationAction(NavigationActionPage.CHECKOUT);
            case DeepLinkUrl.FAVORITES:
                // For navigate to favorites list, we need the list id of it.
                const listId = await firstValueFrom(this.getFavoritesListIdUseCase.execute());
                return new NavigationAction(NavigationActionPage.LIST_DETAIL,{listId: listId});
            case DeepLinkUrl.ACCOUNT:
                return new NavigationAction(NavigationActionPage.ACCOUNT);
            case DeepLinkUrl.CATEGORIES:
                return new NavigationAction(NavigationActionPage.CATEGORIES);
            case DeepLinkUrl.PRODUCT_DETAIL:
                return new NavigationAction(NavigationActionPage.PRODUCT_DETAIL, deepLink.parameters);
            case DeepLinkUrl.PROMOTION_DETAIL:
                return new NavigationAction(NavigationActionPage.PROMOTION_DETAIL, deepLink.parameters);
            case DeepLinkUrl.LOGIN:
                return new NavigationAction(NavigationActionPage.LOGIN, deepLink.parameters);
            case DeepLinkUrl.SHARED:
                return new NavigationAction(NavigationActionPage.SHARED, deepLink.parameters);
        }
    }
}
